import { useEffect, useRef, useState } from "react";
import { getFirestoreDb } from "@/lib/firebaseClient";
import { selectFirstCompletedCar } from "@/app/helpers/selectFirstCompletedCar";

const useFirstCompletedCar = (
  finList,
  { enabled = true, initialData = null } = {},
) => {
  // filter(Boolean) wie server-seitig (getInitialCarData): ein leerer/null-fin
  // würde den where("__name__","in",...)-Query-Bau werfen und den Fetch still
  // abbrechen.
  const normalizedFinList = Array.isArray(finList)
    ? finList.filter(Boolean)
    : [];
  const hasFinList = normalizedFinList.length > 0;
  const finListKey = JSON.stringify(normalizedFinList);
  // SSR kann die Karte bereits fertig liefern (initialData aus layout.js).
  // Dann startet die Karte ohne Skeleton und der spätere Firestore-Fetch ist
  // nur ein stiller Refresh im Hintergrund.
  const initialDataRef = useRef(initialData);
  const [carData, setCarData] = useState(initialDataRef.current);
  const [loading, setLoading] = useState(
    () => enabled && hasFinList && !initialDataRef.current,
  );

  useEffect(() => {
    let cancelled = false; // für Safety beim Unmount

    if (!enabled) {
      // Ohne SSR-Daten: Skeleton anzeigen bis der Fetch freigegeben wird.
      // Mit SSR-Daten: Karte steht bereits, nichts anfassen.
      if (!initialDataRef.current) {
        setCarData(null);
        setLoading(hasFinList);
      }
      return () => {
        cancelled = true;
      };
    }

    if (!hasFinList) {
      // Logout/User-Wechsel: SSR-Fallback mit entsorgen, sonst könnte ein
      // späterer Refetch die Karte des alten Accounts reanimieren.
      initialDataRef.current = null;
      setCarData(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchData = async () => {
      const finListForFetch = JSON.parse(finListKey);

      if (!finListForFetch.length) {
        if (!cancelled) {
          setCarData(null);
          setLoading(false);
        }
        return;
      }

      try {
        const db = await getFirestoreDb();
        const { collection, query, where, getDocs } = await import(
          "firebase/firestore"
        );

        const chunkSize = 10; // Firestore "in" ≤ 10 IDs
        const chunkQueries = [];
        for (let i = 0; i < finListForFetch.length; i += chunkSize) {
          const chunk = finListForFetch.slice(i, i + chunkSize);
          const q = query(
            collection(db, "USERTOFIN"),
            where("__name__", "in", chunk), // filter by Doc-ID
          );
          chunkQueries.push(getDocs(q));
        }

        const snapshots = await Promise.all(chunkQueries);
        const entries = [];
        snapshots.forEach((snap) => {
          snap.forEach((docSnap) =>
            entries.push({ id: docSnap.id, data: docSnap.data() || {} }),
          );
        });

        const chosen = selectFirstCompletedCar(entries);

        if (!cancelled) {
          // Frischer Fetch gewinnt; fällt der ALLERERSTE leer aus, bleibt die
          // SSR-Karte stehen. Danach wird der SSR-Fallback invalidiert, damit
          // spätere Refetches (fins geändert, Auto gelöscht) die Karte wie
          // vor dem SSR-Feature leeren können statt stale Daten zu zeigen.
          setCarData(chosen ?? initialDataRef.current ?? null);
          initialDataRef.current = null;
          setLoading(false);
        }
      } catch (err) {
        console.error("🔥 useFirstCompletedCar error:", err);
        if (!cancelled) setLoading(false);
      }
    };

    if (!initialDataRef.current) {
      setLoading(true);
    }
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [enabled, hasFinList, finListKey]); // stabil bei Array-Änderung

  return { carData, loading };
};

export default useFirstCompletedCar;
